import React, { useMemo } from 'react';
import Header from './Header';
import Footer from './Footer';
import Animation from '../animation/animation';
import { CardBody, CardContainer, CardItem } from './ui/3d-card';
import baristasData from '../data/baristas.json';

/* ─── Aceternity Spotlight ─── */
const Spotlight = ({ className = '' }) => (
  <div
    className={`pointer-events-none absolute z-0 ${className}`}
    style={{
      width: '600px', height: '600px',
      background: 'radial-gradient(ellipse at center, rgba(230,25,51,0.13) 0%, rgba(230,25,51,0.04) 45%, transparent 70%)',
      borderRadius: '50%', filter: 'blur(48px)',
    }}
  />
);

/* ─── Shimmer hover border ─── */
const ShimmerCard = ({ children, className = '' }) => (
  <div className={`relative group/shimmer [transform-style:preserve-3d] ${className}`}>
    <div
      className="absolute -inset-[1px] rounded-xl opacity-0 group-hover/shimmer:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{
        background: 'linear-gradient(135deg, rgba(230,25,51,0.55) 0%, rgba(255,255,255,0.06) 50%, rgba(230,25,51,0.25) 100%)',
        zIndex: 0, borderRadius: 'inherit',
      }}
    />
    <div className="relative z-10 h-full [transform-style:preserve-3d]">{children}</div>
  </div>
);

/* ─── Glowing divider ─── */
const GlowDivider = () => (
  <div className="w-full max-w-2xl mx-auto my-8 relative h-px">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#E61933] to-transparent opacity-60" />
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#E61933] to-transparent blur-sm opacity-30" />
    <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E61933] shadow-[0_0_8px_rgba(230,25,51,0.9)]" />
  </div>
);

/* ─── Section eyebrow badge ─── */


/* ─── Social button ─── */
const SocialBtn = ({ href, label, icon, hoverBg }) => {
  if (!href || href.trim() === "" || href === "/") return null;

  const cleanUrl = href.split(' ')[0].trim();
  const finalHref = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;

  return (
    <a
      href={finalHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      // CRITICAL: stopPropagation ensures the click isn't swallowed by CardContainer tilt
      onClick={(e) => {
        e.stopPropagation();
      }}
      // Secondary fallback handling for strict 3D contexts
      onPointerDown={(e) => {
        if (e.button === 0) { // Main click only
          e.stopPropagation();
          // We let standard anchor href handle standard navigation, stopPropagation ensures standard behavior
        }
      }}
      className="flex items-center justify-center w-7 h-7 rounded-lg border transition-all duration-300 relative z-50 pointer-events-auto"
      style={{
        background: 'rgba(255,255,255,0.08)',
        borderColor: 'rgba(255,255,255,0.15)',
        color: 'rgba(255,255,255,0.9)',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.color = '#fff';
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
        e.currentTarget.style.color = 'rgba(255,255,255,0.35)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {icon}
    </a>
  );
};

const LiIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);
const IgIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);
const GhIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

/* ════════════ Main Page ════════════ */
const BaristasPage = () => {
  const getDirectDriveUrl = (url) => {
    if (!url || !url.includes('drive.google.com')) return url;
    const match = url.match(/(?:id=|d\/)([a-zA-Z0-9_-]+)/);
    return match?.[1] ? `https://drive.google.com/uc?export=view&id=${match[1]}` : url;
  };

  const getOptimizedUrl = (url) => {
    if (!url) return url;
    let f = url.includes('drive.google.com') ? getDirectDriveUrl(url) : url;
    if (f.includes('cloudinary.com') && f.includes('/upload/'))
      return f.replace('/upload/', '/upload/f_auto,q_auto/');
    return f;
  };

  const teamsData = useMemo(() => {
    const order = ['Lead Organisers', 'Organisers', 'Heads', 'Sub-Heads', 'team'];
    const groups = baristasData.reduce((acc, p) => {
      const k = p['Team Name*']?.trim();
      const image = p['Upload Your Document Photo*']?.trim();
      
      // Only include baristas who have an image provided
      if (order.includes(k) && image && image !== "") {
        acc[k] = acc[k] || [];
        acc[k].push({
          name: p['Full Name*'], position: p['Position / Role*'],
          image: image,
          linkedin: p['LinkedIn Profile Link'],
          instagram: p['Instagram Profile Link'],
          github: p['GitHub Profile Link'],
        });
      }
      return acc;
    }, {});
    return order
      .filter(t => groups[t]?.length > 0)
      .map((t, i) => ({
        title: t === 'team' ? 'Team' : t,
        members: groups[t], index: i,
      }));
  }, []);

  return (
    <div className="baristas-page relative overflow-x-hidden min-h-screen flex flex-col bg-black">

      {/* Fixed background */}
      <div className="fixed top-0 left-0 w-full h-screen z-0 pointer-events-none">
        <Animation className="w-full h-full" />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 35%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0.55) 72%, rgba(0,0,0,0.82) 87%, rgba(0,0,0,1) 100%)',
        }} />
        <Spotlight className="top-16 left-[8%]" />
        <Spotlight className="top-[38%] right-[4%]" />
        <Spotlight className="bottom-[10%] left-[40%]" />
      </div>

      <Header />

      <main className="w-[94%] max-w-[1400px] mx-auto relative z-10 pt-36 px-4 md:px-10 lg:px-12 flex flex-col items-center pb-24">

        {/* Hero */}
        <div className="w-full flex flex-col items-center text-center mb-4">

          {/* Array display heading */}
          <h1
            className="text-5xl md:text-7xl lg:text-[88px] font-black text-center uppercase leading-[0.9] mb-5"
            style={{
              fontFamily: "'Array-semibold', 'Space Grotesk', 'Inter', sans-serif",
              letterSpacing: '-0.04em',
              background: 'linear-gradient(165deg, #ffffff 25%, rgba(255,255,255,0.28) 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}
          >
            Our Baristas
          </h1>

          <p className="text-[11px] font-bold tracking-[0.45em] uppercase mb-5" style={{ color: '#E61933' }}>
            The Visionaries Behind the Brew
          </p>

          <p className="text-white/35 font-light text-sm md:text-base max-w-lg leading-relaxed">
            A dedicated collective of innovators and leaders crafting the future of the technology ecosystem.
          </p>

          <GlowDivider />
        </div>

        {/* Team sections */}
        <div className="w-full flex flex-col gap-28 mt-6">
          {teamsData.map((section) => (
            <div key={section.index} className="w-full flex flex-col">

              <div className="flex flex-col mb-12 items-center md:items-start">

                <h2
                  className="text-3xl md:text-5xl font-black text-white uppercase leading-none"
                  style={{ fontFamily: "'Array-semibold', 'Space Grotesk', 'Inter', sans-serif", letterSpacing: '-0.03em' }}
                >
                  {section.title}
                </h2>

              </div>

              {/* Grid */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-5 gap-y-8">
                {section.members.map((barista, idx) => (
                  <CardContainer key={idx} className="inter-var w-full flex justify-center">
                    <ShimmerCard className="w-full max-w-[240px]">
                      <CardBody
                        className="relative w-full rounded-xl p-3.5 border flex flex-col transition-all duration-500 overflow-hidden"
                        style={{
                          background: 'rgba(255,255,255,0.01)',
                          backdropFilter: 'blur(4px)',
                          borderColor: 'rgba(255,255,255,0.08)',
                        }}
                      >
                        <CardItem translateZ="100" className="w-full mb-3">
                          <div
                            className="relative overflow-hidden rounded-lg transform-gpu group/image"
                            style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
                          >
                            <img
                              src={getOptimizedUrl(barista.image) || `https://ui-avatars.com/api/?name=${encodeURIComponent(barista.name)}&background=111&color=E61933&size=300`}
                              width="300" height="300"
                              className={`aspect-square w-full object-cover transition-transform duration-700 group-hover/image:scale-110 group-hover/card:scale-105 ${
                                barista.name?.toLowerCase().includes("aaditri") ? "object-[center_15%]" : "object-top"
                              }`}
                              alt={barista.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(barista.name)}&background=111&color=E61933&size=300`;
                              }}
                            />
                            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)' }} />
                          </div>
                        </CardItem>

                        <CardItem translateZ="60" className="flex flex-col gap-0.5 mb-3 overflow-hidden">
                          <p
                            className="text-sm font-black text-white uppercase leading-tight truncate"
                            style={{ fontFamily: "'Array-semibold', 'Space Grotesk', 'Inter', sans-serif", letterSpacing: '-0.02em' }}
                            title={barista.name}
                          >
                            {barista.name}
                          </p>
                          <p className="text-[9px] font-bold tracking-[0.18em] uppercase truncate" style={{ color: '#E61933' }} title={barista.position}>
                            {barista.position}
                          </p>
                        </CardItem>

                        <div className="w-full h-px mb-3" style={{ background: 'rgba(255,255,255,0.055)' }} />

                        {/* Social Icons only - Updated Fix Applied Here */}
                        <CardItem
                          translateZ="150"
                          className="flex justify-center items-center gap-3 w-full mt-1 relative z-[10] pointer-events-auto"
                        >
                          <SocialBtn href={barista.instagram} label="Instagram" icon={<IgIcon />} hoverBg="linear-gradient(45deg,#f9ce34,#ee2a7b,#6228d7)" />
                          <SocialBtn href={barista.github} label="GitHub" icon={<GhIcon />} hoverBg="#24292e" />
                          <SocialBtn href={barista.linkedin} label="LinkedIn" icon={<LiIcon />} hoverBg="#0077B5" />
                        </CardItem>
                      </CardBody>
                    </ShimmerCard>
                  </CardContainer>
                ))}
              </div>

              {section.index < teamsData.length - 1 && (
                <div className="w-full mt-20 h-px" style={{
                  background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.05) 70%, transparent)',
                }} />
              )}
            </div>
          ))}
        </div>
      </main>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
        .baristas-page * { box-sizing: border-box; }
      `}</style>
    </div>
  );
};

export default BaristasPage;
